import styles from "./Cart.module.css";
import React from "react";
import { PencilLine, ShoppingBasket } from "lucide-react";
import { useTranslation } from "react-i18next";
import CartProductCard from "../ProductCards/Cart/CartProductCard";
import CartVenueCard from "../ProductCards/Cart/CartVenueCard";
import CartExtraCard from "../ProductCards/Cart/CartExtraCart";
import CartActivityCard from "../ProductCards/Cart/CartActivityCard";
import { venues } from "../../constants/venues";
import { timeToMinutes } from "../../utils/calendar";
import { getCartBlockPrice } from "../../utils/cart";
import { formatCurrency, formatCurrencyFromCzk } from "../../utils/prices";
import { czk_eur_rate } from "../../constants/prices";
import type { CartData, CartItem } from "../../types/cart.types";
import type { Subscriber } from "../../types/auth.types";

type CartBlockListProps = {
    groupedCart: Record<string, Record<string, CartItem[]>>;
    cartData: CartData | null;
    orderType: string;
    user?: Subscriber;
    selectedCurrency: string;
    blockLookup: {
        sales: any[];
        packages: any[];
        storage: any[];
    };
    allProducts: any[];
    packages: any[];
    items: any[];
    activities: any[];
    transport: number;
    isDesktop: boolean;
    onEditTime: (date: string, time: string) => void;
};

const CartBlockList = ({
    groupedCart,
    cartData,
    orderType,
    user,
    selectedCurrency,
    blockLookup,
    allProducts,
    packages,
    items,
    activities,
    transport,
    isDesktop,
    onEditTime,
}: CartBlockListProps) => {
    const { t } = useTranslation();
    const showEmpty = !cartData?.totals || cartData?.totals.total_amount === 0;
    const getBlockPercentage = (currPrice: number, minPrice: number) => (currPrice / minPrice) * 100;

    return (
        <>
            {isDesktop && (
                <div className={styles.CartTitleSecondary}>
                    <p>
                        {orderType === "demand"
                            ? t("cart.product_section.demand_title")
                            : t("cart.product_section.title")}
                    </p>
                </div>
            )}
            <div className={styles.CartProductSection}>
                <div className={styles.CardProducts}>
                    {showEmpty && (
                        <div className={styles.NoProducts}>
                            <ShoppingBasket />
                            <p>{t("cart.empty")}</p>
                        </div>
                    )}
                    {Object.entries(groupedCart)
                        .sort(([dateA], [dateB]) => new Date(dateA).getTime() - new Date(dateB).getTime())
                        .map(([date, times]) =>
                            Object.entries(times)
                                .sort(([timeA], [timeB]) => {
                                    const minutesA = timeToMinutes(timeA.split("-")[0]);
                                    const minutesB = timeToMinutes(timeB.split("-")[0]);
                                    return minutesA - minutesB;
                                })
                                .map(([time, products], i) => {
                                    const blockPrice = getCartBlockPrice(products, blockLookup);
                                    const minOrd = user?.minimal_order_value ?? 5000;
                                    const percentage = getBlockPercentage(blockPrice, minOrd);
                                    const remaining = Math.max(Math.round(minOrd - blockPrice), 0);
                                    const remainingText = formatCurrencyFromCzk(
                                        remaining,
                                        czk_eur_rate,
                                        selectedCurrency
                                    );

                                    return (
                                        <React.Fragment key={`${date}-${time}`}>
                                            {i === 0 && <p className={styles.Date}>{date}</p>}

                                            <div className={styles.CartDeliveryTime}>
                                                <p className={styles.CartTime}>{time}</p>
                                                <div
                                                    className={styles.CartTimeEdit}
                                                    onClick={() => onEditTime(date, time)}
                                                >
                                                    <PencilLine />
                                                </div>
                                            </div>
                                            <div
                                                className={styles.CartLoader}
                                                style={
                                                    {
                                                        "--after-width": `${Math.min(
                                                            percentage,
                                                            100
                                                        )}%`,
                                                    } as React.CSSProperties
                                                }
                                            ></div>
                                            <p className={styles.CartMinimal}>
                                                {orderType === "demand"
                                                    ? t("cart.min_demand")
                                                    : t("cart.min_order")}{" "}
                                                {remainingText}.
                                            </p>

                                            {products.map((prod, k) => {
                                                if (prod.category === "sales") {
                                                    const product = allProducts.find(
                                                        (p) => p.id === Number(prod.id)
                                                    );
                                                    if (!product) return null;
                                                    return (
                                                        <CartProductCard
                                                            key={`${date}-${time}-${prod.id}-${k}`}
                                                            product={product}
                                                            date={date}
                                                            time={time}
                                                        />
                                                    );
                                                }
                                                if (prod.category === "package") {
                                                    const product = packages.find(
                                                        (p) => p.id === Number(prod.id)
                                                    );
                                                    if (!product) return null;
                                                    return (
                                                        <CartProductCard
                                                            key={`${date}-${time}-${prod.id}-${k}`}
                                                            product={product}
                                                            date={date}
                                                            time={time}
                                                            type="package"
                                                        />
                                                    );
                                                }
                                                if (prod.category === "venue") {
                                                    const product = venues.find(
                                                        (p) => p.id === Number(prod.id)
                                                    );
                                                    if (!product) return null;
                                                    return (
                                                        <CartVenueCard
                                                            key={`${date}-${time}-${prod.id}-${k}`}
                                                            venue={product}
                                                            date={date}
                                                            time={time}
                                                        />
                                                    );
                                                }
                                                if (prod.category === "storage") {
                                                    const product = items.find(
                                                        (p) => p.id === Number(prod.id)
                                                    );
                                                    if (!product) return null;
                                                    return (
                                                        <CartExtraCard
                                                            key={`${date}-${time}-${prod.id}-${k}`}
                                                            product={product}
                                                            date={date}
                                                            time={time}
                                                        />
                                                    );
                                                }
                                                if (prod.category === "activities") {
                                                    const product = activities.find(
                                                        (p) => p.id === Number(prod.id)
                                                    );
                                                    if (!product) return null;
                                                    return (
                                                        <CartActivityCard
                                                            key={`${date}-${time}-${prod.id}-${k}`}
                                                            product={product}
                                                            date={date}
                                                            time={time}
                                                        />
                                                    );
                                                }
                                                return null;
                                            })}

                                            <div className={styles.SingleDelivery}>
                                                <p>
                                                    {cartData?.delivery_item?.name?.trim()
                                                        ? cartData.delivery_item.name
                                                        : t("cart.summary_section.delivery")}
                                                </p>
                                                <p>
                                                    {t("products.no_dph_short")}{" "}
                                                    {formatCurrency(transport, selectedCurrency)}
                                                </p>
                                            </div>
                                        </React.Fragment>
                                    );
                                })
                        )}
                </div>
            </div>
        </>
    );
};

export default CartBlockList;
